import { ActionButtonV2 } from "components/Table/components/ActionButtons";
import DataTable from "components/v2/DataTable";
import { getColumns, getPDFColumns, getXlsColumns } from "./constant";
import { useAppSelector } from "hooks/store";
import { selectUserData } from "store/auth/authSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { filterColumnsV2 } from "utils/filterColumns";
import { IProviderCheckList } from "types/IProviderCheckList";
import { ELocalSavedFilters, getDefaultFilters, ISavedDefaultFilters, setDefaultFiltersV2, tableFiltersWithProviders } from "utils/defaultFilters";
import useUrlQuery from "hooks/useUrlQuery";
import { VDPResultsUrlParams } from "utils/urlParams";
import useGetReports, { EReportsModules, getReportsAsync, IAPIRecentActivityReportV2 } from "api/useGetReports";
import { IDropdownFilterKeyPair, PaginationState } from "components/Table";
import formatOrderingAPI from "utils/formatOrderingAPI";
import { TRecentActivityReportOrderKeys } from "api/useGetSales";
import { format, set } from "date-fns";
import DateRange, { IDateRangeProps } from "components/DateRange";
import { allTime, commonDateRangesV2, getDateRangeByKey, IDateRange } from "constants/commonDateRanges";
import apiDateFormat from "constants/apiDateFormat";
import { useDebounce } from "hooks";
import { ColumnSizingState, SortingState, VisibilityState } from "@tanstack/react-table";
import { ESortOrder, ISortState } from "components/Table/types";
import { Box, Text } from "@chakra-ui/react";
import { CustomizeProvider } from "components/v2/CustomizeProvider";
import DolSelect from "components/v2/Select/DolSelect";
import { toast } from "react-toastify";
import { formattedDateRange, onGeneratePDFDefaultType, reportNaming, resultScoreConverterToNumber } from "utils/helper";
import { TActionProps, TGeneratePdfFunction, TGenerateXLSFunction } from "components/Table/components/ActionButtons/types";
import usePostUsers, { EUserModules } from "api/usePostUsers";
import Typography from "components/Typography";
import { TDateRangeOption } from "types/APIParameters";

type TSavedFilters = ISavedDefaultFilters[ELocalSavedFilters.VDPResultTable];

export const VDPResultsV2 = () => {

    const userAuth = useAppSelector(selectUserData);

    const { mutate: savePagePreference } = usePostUsers<EUserModules.PagePreference>({
      module: EUserModules.PagePreference,
    });

    const { getParam, getMultipleParams, setMultipleParams, removeMultipleParams } = useUrlQuery<VDPResultsUrlParams>();

    const startDateParams = getParam("startDate");
    const endDateParams = getParam("endDate");
    const dolRangeParams = getParam("dolRange");
    const sourceParams = getMultipleParams("source");
    const resultScoreParams = getParam("resultScore");
    const sortByParams = getParam("sortBy");
    const searchParams = getParam("search");
    const selectedDolRangeParams = getParam("selectedDolRange");
    const missingPhotosParams = getParam("is_missing_photos");
    const lessThan50VDPsParams = getParam("is_less_than_50_vdps");
    const dolMinParams = getParam("dol_min");
    const dolMaxParams = getParam("dol_max");
    const dateRangeParams = getParam("date_range");
    const isLessThan5AscLeadsParams = getParam("is_less_than_5_asc_leads");

    console.log('dolRangeParams', dolRangeParams);

    const defaultFilters = getDefaultFilters<TSavedFilters>(
      ELocalSavedFilters.VDPResultTable,
      tableFiltersWithProviders
    );

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
      defaultFilters.columnVisibility && Object.keys(defaultFilters.columnVisibility).length > 0 ? defaultFilters.columnVisibility : { vin: false }
    );

    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(defaultFilters.columnSizing || {});

    const [dateRange, setDateRange] = useState<IDateRangeProps["value"]>(
        // isFromNeedsAttention ? needsAttentionDateRange : defaultFilters.dateRange
        {
          startDate: startDateParams ? new Date(startDateParams) : dateRangeParams ? getDateRangeByKey(dateRangeParams)?.startDate : allTime.startDate,
          endDate: endDateParams ? new Date(endDateParams) : dateRangeParams ? getDateRangeByKey(dateRangeParams)?.endDate : allTime.endDate,
          key: dateRangeParams || 'all_time'
        }
    );

    const dolOptions: IDropdownFilterKeyPair[] = useMemo(() => [
        {
            label: 'DOL',
            value: '0',
        },
        {
            label: 'Over 7',
            value: '7',
        },
        {
            label: 'Over 15',
            value: '15',
        },
        {
            label: 'Over 30',
            value: '30',
        },
        {
            label: 'Over 60',
            value: '60',
        },
    ], []);

    const findOption = selectedDolRangeParams ? dolOptions.find((option) => option.value === parseInt(selectedDolRangeParams)) : dolOptions[0];

    const [selectedDol, setSelectedDol] = useState<IDropdownFilterKeyPair>(findOption ?? dolOptions[0]);

    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(defaultFilters.pageSize);

    const [sortBy, setSortBy] = useState<SortingState>(sortByParams && typeof sortByParams === 'string' ? [{
        id: sortByParams.replace("-", "") as TRecentActivityReportOrderKeys,
        desc: sortByParams.startsWith("-") ? true : false,
    }] : [{ id: 'vdps', desc: true }]);

    const [providers, setProviders] = useState<IProviderCheckList[]>(
        defaultFilters.providers
    );
    
    const [quickSearch, setQuickSearch] = useState<string>(searchParams ?? "");

    const quickSearchApi = useDebounce<string>(quickSearch, 350);
  
    useMemo(() => {

      // let providers = defaultFilters.providers;

      console.log("userAuth.dealerV2.third_party_provider", userAuth.dealerV2.third_party_provider);

      const updatedProviders = [
            ...(userAuth.dealerV2.third_party_provider
              // include 'cgr' for all dealers and lite dealers
              .filter((provider) => userAuth.dealerV2.is_lite ? provider.key === 'CGR' : true)
              .map((provider) => {
                const defaultProvider = defaultFilters.providers.find(({ id }) => id === provider.key);
                return {
                    id: provider.key,
                    label: provider.label,
                    value: sourceParams?.includes(provider.key) || (defaultProvider ? defaultProvider.value : false), // Default to false for other providers
                };
            })),
        ] as IProviderCheckList[];

        setProviders(updatedProviders);

      // if (sourceParams?.length) {
      //     providers = userAuth.dealerV2.third_party_provider.map((provider) => ({
      //         id: provider.key,
      //         label: provider.label,
      //         value: sourceParams?.includes(provider.key) || false,
      //     })) as IProviderCheckList[]; 
      // }
      
      // setProviders(providers);

    }, [userAuth.dealerV2.third_party_provider]);

    const sortedBy: ISortState = useMemo(() => {

        if (!sortBy.length) {
            return {
                column: 'dol',
                order: ESortOrder.Ascending,
            };
        }
        return {
            column: sortBy[0].id as TRecentActivityReportOrderKeys,
            order: sortBy[0].desc ? ESortOrder.Descending : ESortOrder.Ascending,
        };
    }, [sortBy]);

    const fetchParameters = useMemo(
        () => ({
            module: EReportsModules.RecentActivityReportV2,
            params: {
            ...(selectedDol.value === 0 && {
                dol_max: dolRangeParams ? isNaN(dolRangeParams.split('-').map(Number)[1]) ? 2000 : dolRangeParams.split('-').map(Number)[1] : 2000,
                dol_min: dolRangeParams ? dolRangeParams.split('-').map(Number)[0] : 0,
            }),
            ...(selectedDol && {
                dol_max: 2000,
                dol_min: selectedDol.value,
            }),
            ...(!selectedDolRangeParams && dolMinParams && {
                dol_min: dolMinParams ? parseInt(dolMinParams) : undefined,
            }),
            ...(!selectedDolRangeParams && dolMaxParams && {
                dol_max: dolMaxParams ? parseInt(dolMaxParams) : undefined,
            }),
            date_from: format(dateRange.startDate, apiDateFormat),
            date_to: format(dateRange.endDate, apiDateFormat),
            dealership_uid: userAuth.dealerV2.uid,
            page: page + 1,
            page_size: pageSize,
            // order,
            ordering: formatOrderingAPI<TRecentActivityReportOrderKeys>(sortedBy),
            search: quickSearchApi,
            source: providers.filter(({ value }) => value).map(({ id }) => id),
            ...(resultScoreParams && {
                result_score: resultScoreConverterToNumber(resultScoreParams),
            }),
            inventory: userAuth.inventory.value,
            ...(missingPhotosParams ? {
                is_missing_photos: missingPhotosParams,
            } : {
                is_missing_photos: undefined
            }),
            ...(lessThan50VDPsParams ? {
                is_less_than_50_vdps: lessThan50VDPsParams,
                dol_min: dolMinParams ? parseInt(dolMinParams) : undefined,
                dol_max: dolMaxParams ? parseInt(dolMaxParams) : undefined,
            } : {
                is_less_than_50_vdps: undefined
            }),
            ...(isLessThan5AscLeadsParams ? {
                is_less_than_5_asc_leads: isLessThan5AscLeadsParams,
            } : {
                is_less_than_5_asc_leads: undefined
            }),
            },
        }),
        [selectedDol, dolRangeParams, dolMinParams, dolMaxParams, dateRange.startDate, dateRange.endDate, userAuth.dealerV2.uid, userAuth.inventory.value, page, pageSize, sortedBy, quickSearchApi, providers, resultScoreParams, missingPhotosParams, lessThan50VDPsParams, isLessThan5AscLeadsParams]
    );

    const { data, isFetching } = useGetReports<EReportsModules.RecentActivityReportV2>(fetchParameters);

    useEffect(() => {

        const selectedProviders = providers.filter(({ value }) => value).map(({ id }) => id);
    
        setMultipleParams({
          // startDate: format(dateRange.startDate, apiDateFormat),
          // endDate: format(dateRange.endDate, apiDateFormat),
          ...(!dolRangeParams && selectedDol && {
            dolRange: `${selectedDol.value}-2000`,
          }),
          ...(dolRangeParams && {
            dolRange: dolRangeParams,
          }),
          source: selectedProviders,
          selectedDolRange: (selectedDol as IDropdownFilterKeyPair).value.toString(),
          inventory: userAuth.inventory.value,
          dealership_uid: userAuth.dealerV2.uid,
          ...(!dateRange.key ? {
                startDate: format(dateRange.startDate, apiDateFormat),
                endDate: format(dateRange.endDate, apiDateFormat),
            } : {
                date_range: dateRangeParams || dateRange.key || 'all_time',
            })
        });
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      // dateRange.startDate, 
      // dateRange.endDate, 
      selectedDol.value, 
      providers, 
      userAuth.inventory.value,
      userAuth.dealerV2.uid
    ]
    );
    
    // useEffect(() => {
    //     setDefaultFiltersV2<TSavedFilters>(ELocalSavedFilters.VDPResultTable, {
    //         dateRange,
    //         pageSize,
    //         providers,
    //         columnVisibility,
    //         columnSizing,
    //     });
    // }, [dateRange, pageSize, providers, columnVisibility, columnSizing]);
    
    const onGenerateXLSX: TActionProps<TGenerateXLSFunction> = useCallback(
        async (generateXLS, setIsLoading) => {
          setIsLoading(true);
    
          try {
            let hasNextPage = true;
            let collectedResults: IAPIRecentActivityReportV2[] = [];
            // let mapOffset = 0;
    
            let tempPage = 1;
    
            do {
              const { next, results } =
                await getReportsAsync<EReportsModules.RecentActivityReportV2>({
                  ...fetchParameters,
                  params: {
                    ...fetchParameters.params,
                    page_size: 1000,
                    page: tempPage,
                  },
                })();
              collectedResults = [...collectedResults, ...results];
    
              hasNextPage = !!next;
              tempPage++;
            } while (hasNextPage);
    
            // filter providers
            const filteredProviders = providers.filter(({ value }) => value).map((providers) => {
              // cgr_deal_rating
              // atc_deal_rating
              // ccm_deal_rating
              return providers.id.toLowerCase();
            });
    
            // create a filter in pdfColumns to filter out the columns that are not in the filteredProviders with a same prefix in the key deal_rating then all other key should include without the deal_rating
            const filteredXlsColumns = getXlsColumns(userAuth.inventory.value, userAuth.dealerV2, columnVisibility).filter(({ key }) => {
              if (key.includes('_deal_rating')) {
                return filteredProviders.includes(key.split('_deal_rating')[0]);
              }
              return true;
            });
    
            const bodyFormatted = collectedResults.map((values) => {
              return filteredXlsColumns.map(
                ({ func, key }) =>
                  func?.(values[key], values) || (values[key] as string)
              );
            });

            const dateRangeTitle = formattedDateRange(dateRange);
    
            const filename = reportNaming({
                dealersshipName: userAuth.dealerV2.name,
                report: 'Comprehensive View',
                inventory: userAuth.inventory.value,
                extension: 'xlsx'
            });

            generateXLS(
              {
                head: [filteredXlsColumns.map(({ label }) => label)],
                body: bodyFormatted,
                formattedDate: dateRangeTitle,
                title: `VDP Results`,
              },
              filename
            );
            setIsLoading(false);
          } catch (err) {
            toast.error("Can't export XLS file. Please contact your admin");
          }
        },
        [fetchParameters, providers, userAuth.dealerV2, userAuth.inventory.value, columnVisibility]
      );
    
      const onGeneratePDF: TActionProps<TGeneratePdfFunction> = useCallback(
        async (onGeneratePDF, setIsLoading) => {
          setIsLoading(true);
    
          try {
            let hasNextPage = true;
            let collectedResults: IAPIRecentActivityReportV2[] = [];
            // let mapOffset = 0;
    
            let tempPage = 1;
    
            do {
              const { next, results } =
                await getReportsAsync<EReportsModules.RecentActivityReportV2>({
                  ...fetchParameters,
                  params: {
                    ...fetchParameters.params,
                    page_size: 1000,
                    page: tempPage,
                  },
                })();
              collectedResults = [...collectedResults, ...results];
    
              hasNextPage = !!next;
              tempPage++;
            } while (hasNextPage);
    
            // filter providers
            const filteredProviders = providers.filter(({ value }) => value).map((providers) => {
              // cgr_deal_rating
              // atc_deal_rating
              // ccm_deal_rating
              return providers.id.toLowerCase();
            });
    
            // create a filter in pdfColumns to filter out the columns that are not in the filteredProviders with a same prefix in the key deal_rating then all other key should include without the deal_rating
            const filteredPDFColumns = getPDFColumns(userAuth.inventory.value, userAuth.dealerV2, columnVisibility).filter(({ key }) => {
              if (key.includes('_deal_rating')) {
                return filteredProviders.includes(key.split('_deal_rating')[0]);
              }
              return true;
            });
    
            const bodyFormatted = collectedResults.map((values) => {
              return filteredPDFColumns.map(
                ({ func, key }) =>
                  func?.(values[key], values) || (values[key] as string)
              );
            });
    
            const dateRangeTitle = formattedDateRange(dateRange);
    
            const filename = reportNaming({
                dealersshipName: userAuth.dealerV2.name,
                report: 'Comprehensive View',
                inventory: userAuth.inventory.value,
                extension: 'pdf'
            });
                
            onGeneratePDF(
              {
                head: [filteredPDFColumns.map(({ label }) => label)],
                body: bodyFormatted,
                ...onGeneratePDFDefaultType(`VDP Results`, dateRangeTitle)
              },
              filename,
              {
                compress: true,
                orientation: "landscape",
              }
            );
            setIsLoading(false);
          } catch (err) {
            toast.error("Can't export PDF file. Please contact your admin");
          }
        },
        [dateRange, fetchParameters, providers, userAuth.dealerV2, userAuth.inventory.value, columnVisibility]
    );

    const filteredColumns = useMemo(
        () => filterColumnsV2(getColumns(userAuth.inventory.value, userAuth.dealerV2), providers),
        [providers, userAuth.dealerV2, userAuth.inventory.value]
    );

    const [columnPositions, setColumnPositions] = useState<string[]>(
        defaultFilters.columnPositions || filteredColumns.map((column) => column.id!)
    );

    const onPageChange = useCallback((pagination: PaginationState) => {
        setPage(pagination.pageIndex);
        setPageSize(pagination.pageSize);
    }, []);
    
    const onSort = useCallback((sort: SortingState) => {
        // if (!sort.length) return;
        setSortBy(sort);
        setMultipleParams({
          sortBy: sort.length > 0 ? `${sort[0].desc ? '-' : ''}${sort[0].id}` : '',
        });
    }, []);
    
    const onDateChange = (newValue: [Date, Date], key?: TDateRangeOption) => {
        if (!key) {

          setMultipleParams({
            startDate: format(set(newValue[0], { hours: 0, minutes: 0, seconds: 0 }), apiDateFormat),
            endDate: format(set(newValue[1], { hours: 23, minutes: 59, seconds: 59 }), apiDateFormat),
            date_range: undefined,
          })

        } else {

            removeMultipleParams(['startDate', 'endDate']);

            setMultipleParams({
              date_range: key,
            });
            
        }

        setDateRange({
            startDate: set(newValue[0], { hours: 0, minutes: 0, seconds: 0 }),
            endDate: set(newValue[1], { hours: 23, minutes: 59, seconds: 59 }),
        });
    };

    const onDolChange = (selectedDol: IDropdownFilterKeyPair) => {
      setSelectedDol(selectedDol);
      setSortBy([{ id: 'dol', desc: false }]);
    };

    const onProviderChange = (selectedProvider: IProviderCheckList) => {
        setProviders((prevProviders) =>
            prevProviders.map(provider =>
                provider.id === selectedProvider.id ? { ...provider, value: !provider.value } : provider
            )
        );
        
    };
    
    const onSearch = (search: string) => {
      setPage(0);
      setQuickSearch(search);
      setMultipleParams({
        search
      });
    };
    
    const onSavePreferences = () => {

      const pagePrefrence = setDefaultFiltersV2<TSavedFilters>(ELocalSavedFilters.VDPResultTable, {
          dateRange: {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
              key: dateRangeParams || undefined
          },
          pageSize,
          providers,
          columnVisibility,
          columnSizing,
          columnPositions
      });

      console.log(JSON.parse(pagePrefrence));

      savePagePreference({
        settings: JSON.parse(pagePrefrence),
      }, {
        onError: () => {
          toast.error("Failed to save preferences");
        }, 
        onSuccess: () => {
          toast.success("Page preferences saved successfully");
        }
      });
    };

    const columnSidebarChilrend = () => {
      if (!providers.length || userAuth.inventory.value === 'new') return null;
      return (
        <Box
          display="flex"
          flexFlow="column"
          gap="10px"
        >
          <Text
            fontWeight="600"
          >
            Providers
          </Text>
          <CustomizeProvider 
            providers={providers}
            onChange={onProviderChange}
          />
        </Box>
      );
    };

    const conttroller = () => {
        return (
            <Box
                display="flex"
                gap="15px"
                justifyContent="space-between"
                flex="1"
                alignItems="center"
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap="15px"
                    flex="1"
                >
                    <DolSelect 
                        defaultValue={selectedDol}
                        options={dolOptions}
                        onChange={onDolChange}
                    />
                </Box>
                <Box>
                    <ActionButtonV2 
                        actionConfig={{
                            isDownloadPDF: true,
                            isDownloadXLS: true,
                            isEmailPDF: true,
                            isEmailXLS: true,
                            isPrintPDF: true
                        }}
                        onDownloadPDF={onGeneratePDF}
                        onDownloadXLS={onGenerateXLSX}
                        onGeneratePDF={onGeneratePDF}
                    />
                </Box>
            </Box>
        );
    };

    return (
       <Box 
            display="flex"
            flexDirection="column"
            flex={1}    
        >
          <Box
            marginBottom="18px"
          >
            <Typography
                fontSize="text-xl"
                fontWeight="bold"
            >
                Comprehensive View
            </Typography>
          </Box>
          <Box
              display="flex"
              flexDirection="column"
              gap="15px"
              flex={1}
          >
              <DateRange 
                  onChange={onDateChange}
                  quickOptions={commonDateRangesV2}
                  value={dateRange}
              />
              <DataTable<IAPIRecentActivityReportV2>
                  data={data?.results || []}
                  rowCount={data?.count || 0}
                  onPageChange={onPageChange}
                  sort={sortBy}
                  onSort={onSort}
                  columns={filteredColumns as any}
                  pageIndex={page}
                  pageSize={pageSize}
                  isLoading={isFetching}
                  conttroller={conttroller()}
                  search={quickSearchApi}
                  onSearch={onSearch}
                  pageCount={data?.total_pages}
                  onColumnVisibilityChange={setColumnVisibility}
                  columnVisibility={columnVisibility}
                  onColumnSizingChange={setColumnSizing}
                  columnSizing={columnSizing}
                  showColumns={true}
                  columnsSidebarChildren={!userAuth.dealerV2.is_lite ? columnSidebarChilrend() : undefined}
                  onSavePreferences={onSavePreferences}
                  columnPinning={{
                    left: ['stock_number'],
                  }}
                  columnPositions={columnPositions}
                  onColumnOrderChange={setColumnPositions}
                  // onUpdateData={updateData}
              />
          </Box>
        </Box>
    );
}
